/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragMove,
    CdkDropList,
    CdkDropListGroup,
    moveItemInArray
} from '@angular/cdk/drag-drop';
import {ViewportRuler} from '@angular/cdk/overlay';
import {DragAndDropGridItemModel} from './drag-and-drop-grid-item.model';
import {StatefulComponent} from '../stateful/stateful.component';
import {DragAndDropGridStateModel} from './drag-and-drop-grid-state.model';

@Component({
    selector: 'app-drag-and-drop-grid',
    templateUrl: './drag-and-drop-grid.component.html',
    styleUrls: ['./drag-and-drop-grid.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DragAndDropGridComponent<T, R> extends StatefulComponent<DragAndDropGridStateModel> implements AfterViewInit {

    @Input() items: DragAndDropGridItemModel<T>[] = [];
    @Input() itemsTemplate: TemplateRef<any>;
    @Input() trackBy: TrackByFunction<DragAndDropGridItemModel<T>>;
    @Output() reorderedItems = new EventEmitter<DragAndDropGridItemModel<T>[]>();

    trackItem = (index: number, item: DragAndDropGridItemModel<T>): any => {
        return this.trackBy ? this.trackBy(index, item) : index;
    };

    @Input() allowReorder: boolean;
    @Input() dragAndDropGridConnectedTo: string | string[];
    @Input() dragAndDropGridDisabled: boolean;

    @Input() hasAddButton: boolean;
    @Input() addButtonLabel: string;
    @Input() buttonLabelArticle: string = 'a';
    @Output() addButtonClick = new EventEmitter<void>();

    @ViewChild(CdkDropListGroup) listGroup: CdkDropListGroup<CdkDropList>;
    @ViewChild(CdkDropList) placeholder: CdkDropList;

    public readonly DRAG_AND_DROP_GRID_BOUNDARY = 'drag-and-drop-grid-container';

    constructor(private viewportRuler: ViewportRuler) {
        super();
        this.storeState({target: null, source: null});
    }

    get addButtonDataCy(): string {
        // regex to replace ' ' and '/'
        return `add-button-${(this.addButtonLabel).replace(/[ /]/g, '-')}`;
    }

    ngAfterViewInit(): void {
        this.resetPlaceholder();
    }

    clickOnAddButton(): void {
        this.addButtonClick.emit();
    }

    disableDropGroup(): boolean {
        if (this.allowReorder) {
            return !this.hasAddButton;
        } else {
            return this.dragAndDropGridDisabled;
        }
    }

    /**
     * Acts on drag of an CdkDrag item from the items list.
     * If the item is inside one of the {@link listGroup} elements sets the {@link activeContainer} to it.
     * Allows for the visual effect of the dragged item appearing in the position it will stay if dropped.
     */
    dragMoved(event: CdkDragMove): void {
        if (this.allowReorder) {
            const point = this.getPointerPositionOnPage(event.event);

            this.listGroup._items.forEach(dropList => {
                if (this.isInsideDropListClientRect(dropList, point.x, point.y)) {
                    this.storeState({activeContainer: dropList});
                    return;
                }
            });
        }
    }

    /**
     * For a given item returns its cdkDragBoundary selector
     * @param item {@link DragAndDropGridItemModel}
     * @return the cdkDragBoundary selector string
     */
    getDragItemBoundary(item: DragAndDropGridItemModel<T>): string {
        let boundary = null;
        if (item?.enableDragBoundary) {
            boundary = `.${this.DRAG_AND_DROP_GRID_BOUNDARY}`;
        }
        return boundary;
    }

    /**
     * Acts when a dropEvent occurs on dropList. Resets the placeholder element and updates the {@link items} array order.
     */
    dropListDropped(): void {
        if (this.allowReorder) {
            if (this.state.target == null) {
                return;
            }
            this.resetPlaceholder();
            const parent = this.placeholder.element.nativeElement.parentElement;
            parent.insertBefore(this.state.source.element.nativeElement, parent.children[this.state.sourceIndex]);

            this.storeState({target: null, source: null});

            if (this.state.sourceIndex !== this.state.targetIndex) {
                this.reorderDragAndDropGridItems(this.state.sourceIndex, this.state.targetIndex);
            }
        }
    }

    /**
     * Acts on a cdkDragDropped event for an item from the {@link items}.
     * @param event the triggered {@link CdkDragDrop} event
     */
    onDragDropped(event: CdkDragDrop<T>): void {
        if (this.allowReorder) {
            // If the drop container is connected to the previous container, it means that the drop has not on another
            // element of the cdkDropListGroup, but on a CdkList outside of the component
            if (this.dragDropContainerIsConnectedToPreviousContainer(event)) {
                this.resetPlaceholder();
                // Reset state to initial values
                this.storeState({
                    target: null,
                    source: null,
                    targetIndex: null,
                    sourceIndex: null,
                    activeContainer: null
                });
            }
        }
    }

    /**
     * Determines if and item is allowed to be moved in to a specific drop container
     */
    dropListEnterPredicate = (drag: CdkDrag, dropContainer: CdkDropList): boolean => {
        let canEnter = false;

        if (dropContainer === this.placeholder) {
            canEnter = true;
        }

        if (dropContainer === this.state.activeContainer && this.allowReorder) {
            this.updateStateOnDropListEnterPredicate(drag, dropContainer);
        }

        return canEnter;
    };

    /**
     * Reorders {@link items} according to the {@link state.sourceIndex} and the {@link state.targetIndex}.
     */
    private reorderDragAndDropGridItems(sourceIndex: number, targetIndex: number): void {
        // If it has the add button, dropElement.parentElement.children has that extra element.
        // That element must be removed from the calculated index to match the correct index of the items array.
        if (this.hasAddButton) {
            sourceIndex = sourceIndex - 1;
            targetIndex = targetIndex - 1;
        }
        this.changeArrayItemsOrder(sourceIndex, targetIndex);
        this.reorderedItems.emit(this.items);
    }

    /**
     * Resets the placeholder element.
     */
    private resetPlaceholder(): void {
        const placeholderElement = this.placeholder.element.nativeElement;
        const parent = placeholderElement.parentElement;

        placeholderElement.style.display = 'none';

        parent.removeChild(placeholderElement);
        parent.appendChild(placeholderElement);
    }

    /** Determines the point of the page that was touched by the user. */
    private getPointerPositionOnPage(event: MouseEvent | TouchEvent): any {
        // `touches` will be empty for start/end events so we have to fall back to `changedTouches`.
        const point = this.isTouchEvent(event) ? (event.touches[0] || event.changedTouches[0]) : event;
        const scrollPosition = this.viewportRuler.getViewportScrollPosition();

        return {
            x: point.pageX - scrollPosition.left,
            y: point.pageY - scrollPosition.top
        };
    }

    /**
     * Updates the component state and placeholder element for items reorder
     */
    private updateStateOnDropListEnterPredicate(drag: CdkDrag, dropContainer: CdkDropList): void {
        const placeholderElement = this.placeholder.element.nativeElement;
        const sourceElement = drag.dropContainer.element.nativeElement;
        const dropElement = dropContainer.element.nativeElement;

        const dragIndex = this.indexOf(dropElement.parentElement.children, (this.state.source ? placeholderElement : sourceElement));
        const dropIndex = this.indexOf(dropElement.parentElement.children, dropElement);

        if (!this.state.source) {
            this.storeState({sourceIndex: dragIndex, source: drag.dropContainer});

            placeholderElement.style.width = sourceElement.clientWidth + 'px';
            placeholderElement.style.height = sourceElement.clientHeight + 'px';

            sourceElement.parentElement.removeChild(sourceElement);
        }

        this.storeState({targetIndex: dropIndex, target: dropContainer});

        placeholderElement.style.display = '';
        dropElement.parentElement.insertBefore(placeholderElement, (dropIndex > dragIndex
                ? dropElement.nextSibling : dropElement));

        this.placeholder._dropListRef.enter(drag._dragRef,
                drag.element.nativeElement.offsetLeft, drag.element.nativeElement.offsetTop);
    }

    /** Determines whether an event is a touch event. */
    private isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
        return event.type.startsWith('touch');
    }

    private dragDropContainerIsConnectedToPreviousContainer(event: CdkDragDrop<T>): boolean {
        return Array(event.previousContainer?.connectedTo).flat().some(item => item === event.container?.id);
    }

    private indexOf(collection: HTMLCollection, node: HTMLElement): number {
        return Array.prototype.indexOf.call(collection, node);
    }

    private isInsideDropListClientRect(dropList: CdkDropList, x: number, y: number): boolean {
        const clientRect: { top: number, bottom: number, left: number, right: number } =
                dropList.element.nativeElement.getBoundingClientRect();

        return y >= clientRect.top && y <= clientRect.bottom && x >= clientRect.left && x <= clientRect.right;
    }

    private changeArrayItemsOrder(sourceIndex: number, targetIndex: number): void {
        moveItemInArray(this.items, sourceIndex, targetIndex);
    }

}








